import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as model from "../../../models/MeetingsModel";
import { apiError } from "../../../helpers/ErrorHelper";
import { useAuth } from "../../../context/AuthContext";
import { useNotificationCount } from "../../../context/NotificationCountContext";
import {
  DatePicker,
  MyReactSelect,
  MySelect,
  MyTimePicker,
  Text,
  Editor,
} from "../../inputs/inputs";
import { getData as getClients } from "../../../models/ClientsModel";
import { getData as getContacts } from "../../../models/ContactsModel";
import { getManagers } from "../../../models/UserModel";
import AdminLayout from "../../layout/AdminLayout";
import { Helmet } from "react-helmet";
import PageHeader from "../../common/PageHeader";
import {
  formatDataBeforeDisplay,
  formatDataBeforeSend,
  validorSettings,
  checkPermission,
  isSuperAdminUser,
} from "../../../helpers/GeneralHelper";
import SimpleReactValidator from "simple-react-validator";
import MeetingActions from "./MeetingActions";

const DEFAULT_MODULE = {
  title: "Meetings",
  singularTitle: "Meeting",
  key: "meetings",
  route: "meetings",
  primaryCol: "_meeting_id",
};

const modeOptions = [
  { value: "Virtual", label: "Virtual" },
  { value: "Onsite", label: "Onsite" },
];

const parseTimeToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value)) return value;

  if (typeof value === "string") {
    // HH:mm or HH:mm:ss (24h)
    const match24 = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (match24) {
      const date = new Date(2024, 0, 1, Number(match24[1]), Number(match24[2]), 0, 0);
      return isNaN(date) ? null : date;
    }

    // h:mm a / h:mm:ss a
    const match12 = value
      .trim()
      .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)$/i);
    if (match12) {
      let hours = Number(match12[1]);
      const minutes = Number(match12[2]);
      const meridiem = match12[3].toLowerCase();
      if (meridiem === "pm" && hours < 12) hours += 12;
      if (meridiem === "am" && hours === 12) hours = 0;
      const date = new Date(2024, 0, 1, hours, minutes, 0, 0);
      return isNaN(date) ? null : date;
    }
  }

  const parsed = new Date(Date.parse(`2024-01-01 ${value}`));
  return isNaN(parsed) ? null : parsed;
};

const toHms = (value) => {
  const date = parseTimeToDate(value);
  if (!date) return null;
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}:00`;
};

const addOneMinuteHms = (value) => {
  const date = parseTimeToDate(value);
  if (!date) return null;
  date.setMinutes(date.getMinutes() + 1);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}:00`;
};

const isEndAfterStart = (startValue, endValue) => {
  const start = parseTimeToDate(startValue);
  const end = parseTimeToDate(endValue);
  if (!start || !end) return true;
  return end.getTime() > start.getTime();
};

export const MeetingForm = () => {
  const location = useLocation();
  const { unique_id } = useParams();
  const isEdit =
    location.pathname.slice(
      location.pathname.lastIndexOf("/") + 1,
      location.pathname.length
    ) == "edit";
  const isView =
    location.pathname.slice(
      location.pathname.lastIndexOf("/") + 1,
      location.pathname.length
    ) == "view";
  const isCreate = !isEdit && !isView;

  const module = location.state?.module || DEFAULT_MODULE;
  const [record, setRecord] = useState(location.state?.data || null);

  const inputFields = {
    meeting_logged_by: "",
    meeting_client_id: "",
    meeting_contact_id: "",
    meeting_date: "",
    meeting_start_time: "",
    meeting_end_time: "",
    meeting_type: "",
    meeting_mode: "",
    meeting_agenda: "",
    meeting_discussion_points: "",
    meeting_decisions: "",
    meeting_risks: "",
    meeting_opportunities: "",
  };

  const formatOptions = [
    {
      type: "react-select",
      column: "meeting_logged_by",
      labelColumn: "logged_by_name",
    },
    {
      type: "react-select",
      column: "meeting_client_id",
      labelColumn: "client_name",
    },
    {
      type: "react-select",
      column: "meeting_contact_id",
      labelColumn: "contact_name",
    },
    {
      type: "react-datepicker",
      column: "meeting_date",
    },
    {
      type: "react-timepicker",
      column: "meeting_start_time",
    },
    {
      type: "react-timepicker",
      column: "meeting_end_time",
    },
  ];

  const { setNotificationCount } = useNotificationCount();
  const navigate = useNavigate();
  const { state } = useAuth();
  let user_data = state.user_data;
  const isAdmin =
    user_data?.urole_type === "admin" || isSuperAdminUser(user_data);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [id, setId] = useState("");
  const [fields, setFields] = useState(inputFields);
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "entry"
  );
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const result = await getClients({
          user_token: user_data.user_token,
          page: 1,
          limit: -1,
        });
        if (!result?.clients) return;
        setClients(
          result.clients.map((client) => ({
            value: client._client_id,
            label: client.client_name,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchManagers = async () => {
      try {
        localStorage.removeItem("managers");
        const response = await getManagers({
          user_token: user_data.user_token,
        });
        if (!response?.managers) return;
        setManagers(
          response.managers
            .map((item) => {
              const name = item.manager_name || `${item.user_first_name || ""} ${item.user_last_name || ""}`.trim();
              const designation = item.designation_name ? ` (${item.designation_name})` : "";
              return {
                value: item._user_id,
                label: `${name}${designation}`,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))
        );
      } catch (error) {
        console.error(error);
      }
    };
    fetchManagers();
  }, [isAdmin]);

  useEffect(() => {
    const fetchContacts = async () => {
      if (!fields.meeting_client_id) {
        setContacts([]);
        return;
      }

      const clientId =
        fields.meeting_client_id?.value || fields.meeting_client_id;

      try {
        const response = await getContacts({
          user_token: user_data.user_token,
          contact_client_id: clientId,
          page: 1,
          limit: -1,
        });

        if (typeof response != typeof undefined) {
          let flag = false;
          const options = (response.client_contacts || []).map((item) => {
            if (
              fields.meeting_contact_id == item._contact_id ||
              fields.meeting_contact_id?.value == item._contact_id
            ) {
              flag = true;
            }
            return {
              value: item._contact_id,
              label: item.contact_name,
            };
          });
          if (!flag && fields.meeting_contact_id) {
            setFields((prev) => ({ ...prev, meeting_contact_id: "" }));
          }
          setContacts(options);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchContacts();
  }, [fields.meeting_client_id]);

  useEffect(() => {
    const loadMeeting = async () => {
      if (record) {
        setId(record[module.primaryCol]);
        setFields(formatDataBeforeDisplay(record, formatOptions));
        setErrors({});
        return;
      }

      if (!unique_id || isCreate) return;

      try {
        const response = await model.getSingleData({
          user_token: user_data.user_token,
          id: unique_id,
        });
        const meeting = response?.meeting || response?.meetings || response;
        if (meeting && (meeting._meeting_id || meeting.meeting_unique_id)) {
          setRecord(meeting);
          setId(meeting[module.primaryCol]);
          setFields(formatDataBeforeDisplay(meeting, formatOptions));
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadMeeting();
  }, [record, unique_id]);

  const validator = useRef(new SimpleReactValidator(validorSettings()));

  const handleStartTimeChange = (value) => {
    setFields((prev) => {
      const next = { ...prev, meeting_start_time: value };
      if (value && prev.meeting_end_time && !isEndAfterStart(value, prev.meeting_end_time)) {
        next.meeting_end_time = null;
      }
      return next;
    });
    setErrors((prev) => ({
      ...prev,
      meeting_start_time: "",
      meeting_end_time: "",
    }));
  };

  const handleEndTimeChange = (value) => {
    if (value && fields.meeting_start_time && !isEndAfterStart(fields.meeting_start_time, value)) {
      setErrors((prev) => ({
        ...prev,
        meeting_end_time: "End time must be after start time",
      }));
      toast.error("End time must be after start time");
      return;
    }
    setFields((prev) => ({ ...prev, meeting_end_time: value }));
    setErrors((prev) => ({ ...prev, meeting_end_time: "" }));
  };

  const syncDetailEditors = (currentFields) => {
    const detailColumns = [
      "meeting_agenda",
      "meeting_discussion_points",
      "meeting_decisions",
      "meeting_risks",
      "meeting_opportunities",
    ];
    const updates = {};
    detailColumns.forEach((column) => {
      const wrap = document.querySelector(`[data-editor-column="${column}"]`);
      const wysiwyg = wrap?.querySelector(".jodit-wysiwyg");
      if (wysiwyg) {
        updates[column] = wysiwyg.innerHTML;
      }
    });
    return { ...currentFields, ...updates };
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!validator.current.allValid()) {
      toast.error("Please check the form errors!");
      return;
    }

    if (isAdmin && activeTab === "entry") {
      const amId =
        fields.meeting_logged_by?.value || fields.meeting_logged_by || "";
      if (!amId) {
        setErrors((prev) => ({
          ...prev,
          meeting_logged_by: "Account Manager is required",
        }));
        toast.error("Please select an Account Manager");
        return;
      }
    }

    if (
      fields.meeting_start_time &&
      fields.meeting_end_time &&
      !isEndAfterStart(fields.meeting_start_time, fields.meeting_end_time)
    ) {
      setErrors((prev) => ({
        ...prev,
        meeting_end_time: "End time must be after start time",
      }));
      toast.error("End time must be after start time");
      return;
    }

    validator.current.hideMessages();
    setLoading(true);

    try {
      setErrors({});
      // Jodit only commits to React state on blur; Save can fire first.
      let fieldsToSave =
        activeTab === "details" ? syncDetailEditors(fields) : { ...fields };

      // Always send times as HH:mm:ss — never Date objects / NaN strings
      const startHms = toHms(fieldsToSave.meeting_start_time);
      const endHms = toHms(fieldsToSave.meeting_end_time);
      fieldsToSave = {
        ...fieldsToSave,
        meeting_start_time: startHms || "",
        meeting_end_time: endHms || "",
      };

      // Don't wipe previously saved times when updating Details with empty/invalid times
      if (activeTab === "details" && record) {
        const recordStart = toHms(record.meeting_start_time);
        const recordEnd = toHms(record.meeting_end_time);
        if (!fieldsToSave.meeting_start_time && recordStart) {
          fieldsToSave.meeting_start_time = recordStart;
        }
        if (!fieldsToSave.meeting_end_time && recordEnd) {
          fieldsToSave.meeting_end_time = recordEnd;
        }
      }

      if (activeTab === "details") {
        setFields((prev) => ({
          ...prev,
          ...fieldsToSave,
          meeting_start_time: fieldsToSave.meeting_start_time || null,
          meeting_end_time: fieldsToSave.meeting_end_time || null,
        }));
      }

      const formData = formatDataBeforeSend(
        fieldsToSave,
        inputFields,
        formatOptions
      );

      let response;
      if (record && id) {
        response = await model.update(user_data.user_token, id, formData);
      } else {
        response = await model.create(user_data.user_token, formData);
      }

      if (response.status) {
        setNotificationCount(localStorage.getItem("unread_notification_count"));
        toast.success(response.message);

        if (response.data?.[module.key]) {
          model.saveToStorage(
            response.data[module.key],
            response.data.pagination
          );
        }

        const saved =
          response.data?.meeting ||
          (Array.isArray(response.data?.[module.key])
            ? response.data[module.key].find(
                (m) =>
                  m.meeting_unique_id === unique_id ||
                  m._meeting_id === id
              ) || response.data[module.key][0]
            : null);

        if (!record && saved?.meeting_unique_id) {
          navigate(`/meetings/${saved.meeting_unique_id}/edit`, {
            state: { data: saved, module, activeTab: "details" },
          });
        } else if (saved) {
          setRecord(saved);
          setId(saved[module.primaryCol]);
          setFields(formatDataBeforeDisplay(saved, formatOptions));
          if (activeTab === "entry") {
            setActiveTab("details");
          } else if (activeTab === "details") {
            setActiveTab("actions");
          }
        } else if (!isCreate) {
          if (activeTab === "entry") {
            setActiveTab("details");
          } else if (activeTab === "details") {
            setActiveTab("actions");
          }
        } else {
          navigate("/" + module.route);
        }
      } else {
        setErrors(response.error || {});
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token == 1) {
        navigate("/re-login");
      }
    }

    setLoading(false);
  };

  const meetingPk = id || record?._meeting_id;
  const showTabs = !isCreate && !!meetingPk;

  const accountLabel =
    fields.meeting_client_id?.label || record?.client_name || "";
  const pocLabel =
    fields.meeting_contact_id?.label || record?.contact_name || "";
  const modeLabel = fields.meeting_mode || record?.meeting_mode || "";
  const amLabel =
    fields.meeting_logged_by?.label || record?.logged_by_name || "";

  const pageTitle = isView ? "View" : isEdit ? "Edit" : "Add";
  const pageSubtitle = isCreate
    ? "Capture account, schedule and meeting basics"
    : activeTab === "details"
      ? "Document agenda, decisions, risks and opportunities"
      : activeTab === "actions"
        ? "Track follow-ups and owners for this meeting"
        : "Update meeting schedule and participants";

  return (
    <AdminLayout>
      <Helmet>
        <title>
          {pageTitle} {module.singularTitle} - Digital CMS
        </title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid meeting-form-shell cms-form-shell">
          <PageHeader module={module} />

          <div className="row">
              <div className="col-lg-9">
                <div className="card custom-card meeting-main-card">
                  <div className="card-header">
                    <div className="card-title">
                      {pageTitle} {module.singularTitle}
                    </div>
                    <p className="meeting-header-sub">{pageSubtitle}</p>
                  </div>

                  {showTabs && (
                    <div className="meeting-tabs">
                      <button
                        type="button"
                        className={`meeting-tab-btn${activeTab === "entry" ? " active" : ""}`}
                        onClick={() => setActiveTab("entry")}
                      >
                        <i className="bi bi-calendar2-event"></i>
                        Entry
                      </button>
                      <button
                        type="button"
                        className={`meeting-tab-btn${activeTab === "details" ? " active" : ""}`}
                        onClick={() => setActiveTab("details")}
                      >
                        <i className="bi bi-journal-text"></i>
                        Details
                      </button>
                      <button
                        type="button"
                        className={`meeting-tab-btn${activeTab === "actions" ? " active" : ""}`}
                        onClick={() => setActiveTab("actions")}
                      >
                        <i className="bi bi-list-check"></i>
                        Action Tracker
                      </button>
                    </div>
                  )}

                  <div className="card-body">
                    {activeTab === "actions" && showTabs ? (
                      <MeetingActions
                        meetingId={meetingPk}
                        isView={isView}
                        moduleRoute={module.route}
                      />
                    ) : (
                      <form onSubmit={submit}>
                        {(isCreate || activeTab === "entry") && (
                          <>
                            <div className="meeting-section">
                              <div className="meeting-section-title">
                                <span className="meeting-section-icon entry">
                                  <i className="bi bi-building"></i>
                                </span>
                                Account &amp; POC
                              </div>
                              <div className="row gy-2">
                                {isAdmin && (
                                  <div className="col-xl-12 mb-3">
                                    <MyReactSelect
                                      options={managers}
                                      column="meeting_logged_by"
                                      required={true}
                                      label="Account Manager"
                                      fields={fields}
                                      setFields={setFields}
                                      errors={errors}
                                      disabled={isView}
                                    />
                                  </div>
                                )}
                                <div className="col-xl-6 mb-3">
                                  <MyReactSelect
                                    options={clients}
                                    column="meeting_client_id"
                                    required={true}
                                    label="Account Name"
                                    fields={fields}
                                    setFields={setFields}
                                    errors={errors}
                                    disabled={isView}
                                  />
                                </div>
                                <div className="col-xl-6 mb-3">
                                  <MyReactSelect
                                    options={contacts}
                                    column="meeting_contact_id"
                                    required={true}
                                    label="Client Name (POC)"
                                    fields={fields}
                                    setFields={setFields}
                                    errors={errors}
                                    disabled={isView}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="meeting-section">
                              <div className="meeting-section-title">
                                <span className="meeting-section-icon schedule">
                                  <i className="bi bi-clock-history"></i>
                                </span>
                                Schedule
                              </div>
                              <div className="row gy-2">
                                <div className="col-xl-4 mb-3">
                                  <DatePicker
                                    column="meeting_date"
                                    label="Meeting Date"
                                    required={true}
                                    fields={fields}
                                    setFields={setFields}
                                    errors={errors}
                                    disabled={isView}
                                  />
                                </div>
                                <div className="col-xl-4 mb-3">
                                  <MyTimePicker
                                    column="meeting_start_time"
                                    label="Start Time"
                                    fields={fields}
                                    setFields={setFields}
                                    errors={errors}
                                    disabled={isView}
                                    maxTime={
                                      fields.meeting_end_time
                                        ? toHms(fields.meeting_end_time)
                                        : null
                                    }
                                    onChange={handleStartTimeChange}
                                  />
                                </div>
                                <div className="col-xl-4 mb-3">
                                  <MyTimePicker
                                    column="meeting_end_time"
                                    label="End Time"
                                    fields={fields}
                                    setFields={setFields}
                                    errors={errors}
                                    disabled={isView || !fields.meeting_start_time}
                                    minTime={
                                      fields.meeting_start_time
                                        ? addOneMinuteHms(fields.meeting_start_time)
                                        : null
                                    }
                                    onChange={handleEndTimeChange}
                                  />
                                  {!fields.meeting_start_time && !isView && (
                                    <small className="text-muted">
                                      Select start time first
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="meeting-section">
                              <div className="meeting-section-title">
                                <span className="meeting-section-icon meta">
                                  <i className="bi bi-tags"></i>
                                </span>
                                Meeting Meta
                              </div>
                              <div className="row gy-2">
                                <div className="col-xl-6 mb-3">
                                  <Text
                                    column="meeting_type"
                                    label="Meeting Type"
                                    fields={fields}
                                    setFields={setFields}
                                    errors={errors}
                                    disabled={isView}
                                  />
                                </div>
                                <div className="col-xl-6 mb-3">
                                  <MySelect
                                    column="meeting_mode"
                                    label="Mode (Virtual/Onsite)"
                                    options={modeOptions}
                                    fields={fields}
                                    setFields={setFields}
                                    errors={errors}
                                    disabled={isView}
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {showTabs && activeTab === "details" && (
                          <div className="meeting-detail-grid" key={`details-${id || "new"}-${record?.meeting_update_time || ""}`}>
                            <div className="meeting-detail-card agenda span-2">
                              <Editor
                                column="meeting_agenda"
                                label="Agenda"
                                fields={fields}
                                setFields={setFields}
                                errors={errors}
                                disabled={isView}
                                height={180}
                                simpleToolbar={true}
                              />
                            </div>
                            <div className="meeting-detail-card discussion">
                              <Editor
                                column="meeting_discussion_points"
                                label="Discussion Points"
                                fields={fields}
                                setFields={setFields}
                                errors={errors}
                                disabled={isView}
                                height={180}
                                simpleToolbar={true}
                              />
                            </div>
                            <div className="meeting-detail-card decisions">
                              <Editor
                                column="meeting_decisions"
                                label="Decisions Taken"
                                fields={fields}
                                setFields={setFields}
                                errors={errors}
                                disabled={isView}
                                height={180}
                                simpleToolbar={true}
                              />
                            </div>
                            <div className="meeting-detail-card risks">
                              <Editor
                                column="meeting_risks"
                                label="Risks/Issues"
                                fields={fields}
                                setFields={setFields}
                                errors={errors}
                                disabled={isView}
                                height={180}
                                simpleToolbar={true}
                              />
                            </div>
                            <div className="meeting-detail-card opportunities">
                              <Editor
                                column="meeting_opportunities"
                                label="Opportunities"
                                fields={fields}
                                setFields={setFields}
                                errors={errors}
                                disabled={isView}
                                height={180}
                                simpleToolbar={true}
                              />
                            </div>
                          </div>
                        )}

                        {!isView && (
                          <div className="d-none">
                            <button type="submit" id="meeting-form-submit">
                              Save
                            </button>
                          </div>
                        )}
                      </form>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-3">
                <div className="card custom-card meeting-side-card">
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                    {isView && checkPermission(module.route, "edit") ? (
                      <button
                        type="button"
                        className="btn btn-info btn-sm"
                        title="Edit"
                        onClick={() =>
                          navigate(
                            `/meetings/${record?.meeting_unique_id || unique_id}/edit`,
                            {
                              state: { data: record, module },
                            }
                          )
                        }
                      >
                        <i className="bx bxs-edit me-1"></i> Edit
                      </button>
                    ) : activeTab !== "actions" ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-md"
                        disabled={loading}
                        onClick={() =>
                          document.getElementById("meeting-form-submit")?.click()
                        }
                      >
                        {loading ? (
                          <span>
                            <span className="spinner-border spinner-border-sm align-middle"></span>{" "}
                            Saving...
                          </span>
                        ) : (
                          <>
                            <i className="bi bi-check2-circle me-1"></i>
                            Save
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="text-muted small">Actions auto-save</span>
                    )}
                    <Link className="btn btn-light btn-sm" to="/meetings">
                      <i className="bi bi-reply me-1 fw-semibold"></i> Back
                    </Link>
                  </div>
                  <div className="card-body">
                    {(accountLabel || pocLabel || modeLabel || amLabel) && (
                      <div className="mb-3">
                        <div className="fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>
                          Snapshot
                        </div>
                        {amLabel && (
                          <span className="meeting-summary-chip">
                            <i className="bi bi-person-badge"></i>
                            {amLabel}
                          </span>
                        )}
                        {accountLabel && (
                          <span className="meeting-summary-chip">
                            <i className="bi bi-building"></i>
                            {accountLabel}
                          </span>
                        )}
                        {pocLabel && (
                          <span className="meeting-summary-chip">
                            <i className="bi bi-person"></i>
                            {pocLabel}
                          </span>
                        )}
                        {modeLabel && (
                          <span className="meeting-summary-chip">
                            <i className="bi bi-laptop"></i>
                            {modeLabel}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>
                      Progress
                    </div>
                    <ul className="meeting-side-steps">
                      <li
                        className={
                          !isCreate || activeTab === "entry"
                            ? showTabs
                              ? "done"
                              : "active"
                            : ""
                        }
                      >
                        <span className="step-num">1</span>
                        <span>
                          <strong className="d-block text-dark">Meeting Entry</strong>
                          Account, schedule and mode
                        </span>
                      </li>
                      <li
                        className={
                          showTabs
                            ? activeTab === "details"
                              ? "active"
                              : activeTab === "actions"
                                ? "done"
                                : ""
                            : ""
                        }
                      >
                        <span className="step-num">2</span>
                        <span>
                          <strong className="d-block text-dark">Details</strong>
                          Agenda, decisions and risks
                        </span>
                      </li>
                      <li
                        className={
                          showTabs && activeTab === "actions" ? "active" : ""
                        }
                      >
                        <span className="step-num">3</span>
                        <span>
                          <strong className="d-block text-dark">Action Tracker</strong>
                          Owners, due dates and status
                        </span>
                      </li>
                    </ul>

                    {isCreate && (
                      <p className="text-muted small mt-3 mb-0">
                        Save the meeting entry first to unlock Details and Action
                        Tracker.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>
      <Helmet></Helmet>
    </AdminLayout>
  );
};

export default MeetingForm;
